import type { TFunction } from 'i18next';
import { Journals } from '../../../../types/Journals.types';
import {
  MasterlistCheckStatus,
  MasterlistCheckTier,
  MasterlistJournalCheck,
} from '../../../../types/masterlistCheck.types';
import { isMissingData } from './utils';

export type JournalFilter =
  'all' | 'missing_data' | MasterlistCheckStatus | 'ceased';

export const STATUS_LABEL_KEYS: Record<MasterlistCheckStatus, string> = {
  matches: 'agreements.masterlistCheck.status.matches',
  check_issns: 'agreements.masterlistCheck.status.checkIssns',
  confirm_match: 'agreements.masterlistCheck.status.confirmMatch',
  not_found: 'agreements.masterlistCheck.status.notFound',
};
const CEASED_LABEL_KEY = 'agreements.masterlistCheck.status.ceased';

/** The "Show" options that need the check; the summary links to the same ones, in this order. */
export const STATUS_FILTERS = [
  {
    filter: 'check_issns',
    labelKey: STATUS_LABEL_KEYS.check_issns,
    summaryKey: 'checkIssns',
  },
  {
    filter: 'confirm_match',
    labelKey: STATUS_LABEL_KEYS.confirm_match,
    summaryKey: 'confirmMatch',
  },
  {
    filter: 'not_found',
    labelKey: STATUS_LABEL_KEYS.not_found,
    summaryKey: 'notFound',
  },
  { filter: 'ceased', labelKey: CEASED_LABEL_KEY, summaryKey: 'ceased' },
] as const;

/** A journal's check: undefined without a check, or for a journal that isn't saved yet. */
export const checkFor = (
  checksByJournalId: Map<string, MasterlistJournalCheck> | undefined,
  journal: Journals | undefined
) => (journal?._id ? checksByJournalId?.get(journal._id) : undefined);

/** " · Ceased" after the status label, or '' for a journal SciFree doesn't list as ceased. */
export function ceasedSuffix(
  check: MasterlistJournalCheck,
  t: TFunction
): string {
  return check.flags.includes('ceased') ? ` · ${t(CEASED_LABEL_KEY)}` : '';
}

/** The status label with its " · Ceased" suffix, as the modal and the export show it. */
export const statusLabel = (check: MasterlistJournalCheck, t: TFunction) =>
  `${t(STATUS_LABEL_KEYS[check.status])}${ceasedSuffix(check, t)}`;

/**
 * The API makes every repeated row "ambiguous", which hides what its ISSNs say about the entry
 * it was matched to. Work that out again, in the API's order (_issn_tier), so a repeated row
 * still says what to fix. "partial" also stands in for exact: it adds no line when all agree.
 */
function repeatedRowTier(
  je: string | undefined,
  jp: string | undefined,
  me: string | undefined,
  mp: string | undefined
): MasterlistCheckTier {
  if ((je && je === me) || (jp && jp === mp)) return 'partial';
  if (je && jp && je === mp && jp === me) return 'swapped';
  if ((je && je === mp) || (jp && jp === me)) return 'cross';
  return 'ambiguous';
}

/** Same rule as the API's Format.issn_check_digit_ok: mod-11, 10 written as X. */
export function issnCheckDigitOk(issn: string): boolean {
  const fixed = issn.replace(/[^0-9X]/gi, '').toUpperCase();
  if (fixed.length !== 8 || !/^\d{7}$/.test(fixed.slice(0, 7))) return false;
  let total = 0;
  for (let i = 0; i < 7; i++) total += Number(fixed[i]) * (8 - i);
  const check = (11 - (total % 11)) % 11;
  return fixed[7] === (check === 10 ? 'X' : String(check));
}

/**
 * Plain-language issue lines for one journal, phrased from the customer's side.
 * Cosmetic title differences and info-level diffs aren't shown in the table.
 */
export function masterlistIssues(
  check: MasterlistJournalCheck,
  journal: Journals,
  t: TFunction
): string[] {
  const ours = check.masterlist;
  const je = journal.issn_e || undefined;
  const jp = journal.issn_p || undefined;
  const me = ours?.issn_e || undefined;
  const mp = ours?.issn_p || undefined;
  const flags = new Set(check.flags);
  // React escapes the output already; i18next's own escaping would show "&" as "&amp;".
  const issue = (k: string, values: Record<string, unknown> = {}) =>
    t(`agreements.masterlistCheck.issues.${k}`, {
      ...values,
      interpolation: { escapeValue: false },
    });
  // Always name the field, even when the context makes it clear.
  const E = t('agreements.issne') as string;
  const P = t('agreements.issnp') as string;
  const ourIssns = [me && `${E} ${me}`, mp && `${P} ${mp}`]
    .filter(Boolean)
    .join(', ');
  const lines: string[] = [];
  let suggestedName = false;
  let incompleteCovered = false;

  // One line that fixes both fields: moving the ISSN empties the field it was in, so name what
  // goes there too. This also covers the field it moves to being "missing".
  const wrongField = (
    issn: string,
    field: string,
    otherField: string,
    otherIssn?: string
  ) => {
    lines.push(
      otherIssn
        ? issue('wrongFieldWithOther', { issn, field, otherField, otherIssn })
        : issue('wrongField', { issn, field })
    );
    incompleteCovered = true;
  };

  const tier =
    check.tier === 'ambiguous' && flags.has('listed_more_than_once')
      ? repeatedRowTier(je, jp, me, mp)
      : check.tier;

  switch (tier) {
    case 'swapped':
      lines.push(issue('swapped'));
      break;
    case 'cross':
      if (je && je === mp) wrongField(je, P, E, me);
      else if (jp && jp === me) wrongField(jp, E, P, mp);
      break;
    case 'partial': {
      // One ISSN is equal. A missing other one is covered by incomplete_issns.
      if (je && je === me) {
        if (jp && mp && jp !== mp)
          lines.push(issue('issnDiffers', { issn: mp, field: P }));
      } else if (je && me && je !== me) {
        lines.push(issue('issnDiffers', { issn: me, field: E }));
      }
      break;
    }
    case 'title_suggestion':
      // Same title but conflicting ISSNs: repeating the title wouldn't say what to confirm.
      if (!flags.has('title_differs') && ourIssns) {
        lines.push(issue('sameTitleOtherIssns', { issns: ourIssns }));
      } else if (ours?.name) {
        lines.push(issue('possibleMatch', { name: ours.name }));
        suggestedName = true;
      }
      break;
    case 'ambiguous':
      // Their ISSNs hit several entries; a repeated row says "Listed more than once" instead.
      if (ours?.name && !flags.has('listed_more_than_once')) {
        lines.push(issue('severalMatches', { name: ours.name }));
        suggestedName = true;
      }
      break;
    case 'not_found':
      // An invalid check digit turns not_found into Check ISSNs; the typo line says it all.
      if (check.status === 'not_found') lines.push(issue('notFound'));
      break;
  }

  if (flags.has('incomplete_issns') && !incompleteCovered) {
    if (!je && me) lines.push(issue('missingIssn', { issn: me, field: E }));
    if (!jp && mp) lines.push(issue('missingIssn', { issn: mp, field: P }));
  }
  // A match has nothing to fix, as the API says: e.g. their ISSN-P where we have none can be
  // mistyped and still match on ISSN-E.
  if (flags.has('invalid_check_digit') && check.status !== 'matches') {
    // Skip an ISSN the masterlist has in the same field: that typo is ours to clean.
    if (je && je !== me && !issnCheckDigitOk(je))
      lines.push(issue('mistyped', { issn: je, field: E }));
    if (jp && jp !== mp && !issnCheckDigitOk(jp))
      lines.push(issue('mistyped', { issn: jp, field: P }));
  }
  if (flags.has('listed_more_than_once')) {
    lines.push(issue('listedMoreThanOnce'));
  }
  if (flags.has('title_differs') && ours?.name && !suggestedName) {
    lines.push(issue('titleDiffers', { name: ours.name }));
  }
  // The API only reports this diff when we have a model, but the shared diff type allows a list
  // or null.
  check.diffs
    .filter(
      (d) =>
        d.severity === 'warning' &&
        d.field === 'publishing_model' &&
        d.ours
    )
    .forEach((d) =>
      lines.push(
        issue('publishingModelDiffers', { model: [d.ours].flat().join(', ') })
      )
    );
  if (flags.has('ceased')) {
    lines.push(issue('ceased'));
  }
  return lines;
}

/** The filter options that need the masterlist check. */
export const isStatusFilter = (filter: JournalFilter) =>
  filter !== 'all' && filter !== 'missing_data';

function matchesFilter(
  filter: JournalFilter,
  check: MasterlistJournalCheck | undefined
): boolean {
  if (filter === 'ceased') return !!check?.flags.includes('ceased');
  return check?.status === filter;
}

/** The journals the "Show" filter keeps; the table, its count and the export all use this. */
export function applyJournalFilter(
  journals: Journals[],
  filter: JournalFilter,
  checksByJournalId: Map<string, MasterlistJournalCheck>
): Journals[] {
  if (filter === 'all') return journals;
  if (filter === 'missing_data') return journals.filter(isMissingData);
  return journals.filter((journal) =>
    matchesFilter(filter, checkFor(checksByJournalId, journal))
  );
}
